import Dexie, { type Table } from 'dexie';
import type { BidInput, BidResult, PricingConfig } from '@lbhl/shared';

/**
 * On-device storage.
 *
 * Two jobs: hold the cached pricing config so a bid can be priced with no
 * signal, and hold bids until the server has acknowledged them. A bid that
 * exists only here is not lost — it is queued.
 */

export interface StoredBid {
  id: string;
  pricingVersionId: number;
  engineVersion: string;
  input: BidInput;
  /**
   * What the device computed.
   *
   * Typed as the CURRENT shape, which a row written by an older bundle may not
   * actually have — IndexedDB keeps whatever was stored, and nothing migrates it.
   * Read prices through `storedPrices` rather than reaching for `.quotes` directly.
   */
  result: BidResult;
  status: 'draft' | 'final';
  /** Who and where. Synced with the bid so the server can email the customer. */
  customer: {
    name: string;
    email: string | null;
    phone: string | null;
  };
  property: {
    addressLine1: string;
    city: string | null;
    region: string | null;
    postalCode: string | null;
  };
  createdAt: string;
  /**
   * 'pending'  — needs to go to the server
   * 'synced'   — server has it
   * 'blocked'  — server rejected it for a reason retrying will not fix
   */
  sync: 'pending' | 'synced' | 'blocked';
  syncError?: string;
  needsReview?: boolean;
}

export interface CachedPricing {
  key: 'published';
  config: PricingConfig;
  fetchedAt: string;
}

/**
 * The last identity seen on this device.
 *
 * Cached so an admin working offline keeps their margin panel instead of being
 * silently demoted to an estimator's view. This is display state only -- every
 * write is re-authorised by the server against the bearer token.
 */
export interface CachedIdentity {
  key: 'me';
  id: number;
  email: string;
  name: string;
  role: 'estimator' | 'admin';
}

class BidToolDb extends Dexie {
  bids!: Table<StoredBid, string>;
  pricing!: Table<CachedPricing, string>;
  identity!: Table<CachedIdentity, string>;

  constructor() {
    super('lbhl-bid-tool');
    this.version(1).stores({
      bids: 'id, sync, createdAt',
      pricing: 'key',
    });
    this.version(2).stores({
      bids: 'id, sync, createdAt',
      pricing: 'key',
      identity: 'key',
    });
  }
}

export const db = new BidToolDb();

export async function cachePricing(config: PricingConfig): Promise<void> {
  await db.pricing.put({ key: 'published', config, fetchedAt: new Date().toISOString() });
}

export async function readCachedPricing(): Promise<CachedPricing | undefined> {
  return db.pricing.get('published');
}

export async function cacheIdentity(user: Omit<CachedIdentity, 'key'>): Promise<void> {
  await db.identity.put({ ...user, key: 'me' });
}

export async function readCachedIdentity(): Promise<CachedIdentity | undefined> {
  return db.identity.get('me');
}

/**
 * The prices to show for a stored bid, tolerating one an older app saved.
 *
 * This is not hypothetical. A device KEEPS a bid after it syncs (`sync: 'synced'`),
 * and a bid queued on a pre-2.0.0 bundle is refused by the server and kept too, marked
 * `blocked`. Either way IndexedDB can hold a result with no `quotes`, and reading it as
 * though it had them throws — which would take the whole bid list down rather than
 * degrading one row. "Zero bids on the server" says nothing about what is on a phone.
 *
 * `legacy` is true for a single-package result, so the row can say so instead of
 * presenting an old number as if it were one of the three.
 */
export function storedPrices(bid: StoredBid): { legacy: boolean; amounts: number[] } {
  const result = bid.result as unknown as {
    quotes?: ReadonlyArray<{ totalCents?: unknown }>;
    totalCents?: unknown;
  };

  if (Array.isArray(result.quotes)) {
    return {
      legacy: false,
      amounts: result.quotes.map((q) => (typeof q.totalCents === 'number' ? q.totalCents : 0)),
    };
  }

  return {
    legacy: true,
    amounts: [typeof result.totalCents === 'number' ? result.totalCents : 0],
  };
}

export async function putBid(bid: StoredBid): Promise<void> {
  await db.bids.put(bid);
}

export async function pendingBids(): Promise<StoredBid[]> {
  // Ordered oldest-first so a backlog syncs in the order it was created.
  return db.bids.where('sync').equals('pending').sortBy('createdAt');
}

export async function allBids(): Promise<StoredBid[]> {
  const bids = await db.bids.toArray();
  return bids.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function countPending(): Promise<number> {
  return db.bids.where('sync').equals('pending').count();
}

/**
 * Removes a bid from THIS DEVICE only.
 *
 * Refuses a bid that is still pending: that row is the only copy of work the
 * server has never seen, and this is a tidy-up control, not a way to lose a bid.
 * Anything already synced is safely on the server, and anything blocked cannot be
 * sent at all — most often because an admin deleted it — so both are clutter the
 * estimator should be able to clear.
 */
export async function removeLocalBid(id: string): Promise<void> {
  const bid = await db.bids.get(id);
  if (!bid) return;
  if (bid.sync === 'pending') {
    throw new Error('That bid has not synced yet. It would be lost — leave it until it syncs.');
  }
  await db.bids.delete(id);
}
